/**
 * Offline POS (mirror backend):
 * Club managers: see all club events on offline sale (no assignment filter).
 * Club employees: only events whose offlineAssignedUserIds includes their user id.
 * Empty offlineAssignedUserIds ⇒ employees do not see the event on POS (manager still does).
 * Refetch after save; PUT explicit offlineAssignedUserIds from multi-select; normalize ids to strings.
 */

function normalizeAssignedIdEntry(entry) {
  if (entry == null || entry === "") return "";
  if (typeof entry === "string" || typeof entry === "number") {
    return String(entry).trim();
  }
  if (typeof entry === "object") {
    const oid = entry.$oid;
    if (typeof oid === "string") return oid.trim();
    const nested = entry._id ?? entry.id ?? entry.userId;
    if (nested != null && typeof nested === "object" && nested.$oid) {
      return String(nested.$oid).trim();
    }
    if (nested != null && nested !== "") return String(nested).trim();
    if (typeof entry.user === "string" && entry.user.trim()) {
      return entry.user.trim();
    }
    if (entry.user && typeof entry.user === "object") {
      const u = entry.user._id ?? entry.user.id;
      if (u != null) return String(u).trim();
    }
  }
  const asStr = String(entry).trim();
  if (asStr === "[object Object]") return "";
  return asStr;
}

function unwrapOfflineAssignedRaw(ev) {
  if (!ev || typeof ev !== "object") return null;
  const candidates = [
    ev.offlineAssignedUserIds,
    ev.offline_assigned_user_ids,
    ev.offlineSellerUserIds,
    ev.offline_seller_user_ids,
    ev.assignedOfflineEmployeeIds,
    ev.offlineAssignedEmployees,
    ev.posAssignedUserIds,
    ev.pos_assigned_user_ids,
    ev.offlinePosAssignedUserIds,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const v = candidates[i];
    if (Array.isArray(v)) return v;
  }
  const nested = ev.offlineAssignment ?? ev.offline_assignment;
  if (nested && typeof nested === "object") {
    const inner =
      nested.userIds ??
      nested.user_ids ??
      nested.users ??
      nested.offlineAssignedUserIds;
    if (Array.isArray(inner)) return inner;
  }
  return null;
}

export function pickOfflineAssignedUserIds(ev) {
  if (!ev || typeof ev !== "object") return [];
  const raw = unwrapOfflineAssignedRaw(ev);
  if (!Array.isArray(raw)) return [];
  return [
    ...new Set(raw.map(normalizeAssignedIdEntry).filter(Boolean)),
  ];
}

function sameMongoUserId(a, b) {
  const sa = String(a ?? "").trim().toLowerCase();
  const sb = String(b ?? "").trim().toLowerCase();
  if (!sa || !sb) return false;
  return sa === sb;
}

/** For CLUB_EMPLOYEE only — must be explicitly listed; empty assignment list hides event from POS. */
export function employeeMaySellOfflineEvent(ev, employeeUserId) {
  if (!ev || typeof ev !== "object") return false;
  const ids = pickOfflineAssignedUserIds(ev);
  if (ids.length === 0) return false;
  if (employeeUserId == null || employeeUserId === "") return false;
  return ids.some((id) => sameMongoUserId(id, employeeUserId));
}

export function normalizeTicketOfflineSoldOut(ticket) {
  if (!ticket || typeof ticket !== "object") return false;
  return Boolean(ticket.offlineSoldOut ?? ticket.offline_sold_out);
}
